import React, { useEffect, useState } from "react";
import { listClasses, listSubjects, getWeekSubjectPlan, getTimetableRun } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

// Thống kê số tiết THỰC XẾP (đọc từ grid của bản TKB đang xem) so với số tiết
// đã cấu hình ở "Cấu hình môn học" (weekSubjectPlan) của tuần đó — để phát
// hiện nhanh lớp/môn nào bị xếp thiếu/thừa so với cấu hình.
const HappySchoolScheduleStats = ({ runId, weekId }) => {
  const schoolId = getActiveSchoolId();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [onlyMismatched, setOnlyMismatched] = useState(false);

  useEffect(() => {
    if (!schoolId || !runId || !weekId) return;
    setLoading(true);
    setError("");
    Promise.all([listClasses(schoolId), listSubjects(schoolId), getWeekSubjectPlan(schoolId, weekId), getTimetableRun(schoolId, runId)])
      .then(([classes, subjects, wp, run]) => {
        const classById = Object.fromEntries(classes.map((c) => [c.id, c]));
        const subjectById = Object.fromEntries(subjects.map((s) => [s.id, s]));
        const plan = (wp && wp.plan) || {};
        const grid = (run && run.grid) || {};

        // Các môn "ảo" không thuộc Cấu hình môn học (không có số tiết cấu
        // hình để so sánh) — loại khỏi thống kê để không làm sai lệch.
        const EXCLUDED = new Set(["Nghỉ", "HN", "Thi KS"]);
        const isExcluded = (sid) => EXCLUDED.has(sid) || ["Chào cờ", "Sinh hoạt", "HĐTN(CC)", "HĐTN(SH)"].includes((subjectById[sid] || {}).name);

        const actual = {}; // classId -> subjectId -> count
        Object.entries(grid).forEach(([key, val]) => {
          if (!val || !val[0] || isExcluded(val[0])) return;
          const [classId] = key.split("|");
          const subjectId = val[0];
          actual[classId] = actual[classId] || {};
          actual[classId][subjectId] = (actual[classId][subjectId] || 0) + 1;
        });

        const pairKeys = new Set();
        Object.keys(plan).forEach((cid) => Object.keys(plan[cid] || {}).forEach((sid) => pairKeys.add(`${cid}|${sid}`)));
        Object.keys(actual).forEach((cid) => Object.keys(actual[cid] || {}).forEach((sid) => pairKeys.add(`${cid}|${sid}`)));

        const out = [];
        pairKeys.forEach((key) => {
          const [classId, subjectId] = key.split("|");
          const planned = (plan[classId] || {})[subjectId] || 0;
          const scheduled = (actual[classId] || {})[subjectId] || 0;
          out.push({
            classId,
            subjectId,
            className: classById[classId]?.name || classId,
            subjectName: subjectById[subjectId]?.name || subjectId,
            planned,
            scheduled,
            diff: scheduled - planned,
          });
        });
        out.sort(
          (a, b) =>
            a.className.localeCompare(b.className, "vi", { numeric: true }) ||
            a.subjectName.localeCompare(b.subjectName, "vi")
        );
        setRows(out);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [schoolId, runId, weekId]);

  if (loading) return <p>Đang tải...</p>;
  if (error) return <p style={{ color: "#dc2626" }}>{error}</p>;
  if (rows.length === 0) return <p style={{ color: "#6b7280" }}>Chưa có dữ liệu cấu hình môn học hoặc bản xếp lịch cho tuần này.</p>;

  const mismatched = rows.filter((r) => r.diff !== 0);
  const totalPlanned = rows.reduce((s, r) => s + r.planned, 0);
  const totalScheduled = rows.reduce((s, r) => s + r.scheduled, 0);

  return (
    <div>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        So sánh số tiết <b>thực xếp</b> trong bản TKB này với số tiết đã cấu hình ở{" "}
        <b>📖 Cấu hình môn học</b>. Dòng tô đỏ/vàng là (lớp, môn) bị lệch — xếp thiếu hoặc thừa so với cấu hình.
      </p>
      <div style={{ display: "flex", gap: 16, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <div style={statBox}>Tổng tiết cấu hình: <b>{totalPlanned}</b></div>
        <div style={statBox}>Tổng tiết thực xếp: <b>{totalScheduled}</b></div>
        <div style={{ ...statBox, color: mismatched.length ? "#b91c1c" : "#166534" }}>
          Số dòng lệch: <b>{mismatched.length}</b> / {rows.length}
        </div>
        <button type="button" onClick={() => setOnlyMismatched((v) => !v)} style={onlyMismatched ? filterBtnActive : filterBtn}>
          {onlyMismatched ? "✓ Chỉ hiện Thiếu/Thừa" : "Chỉ hiện Thiếu/Thừa"}
        </button>
      </div>
      <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
        <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
          <thead>
            <tr>
              <th style={th}>Lớp</th>
              <th style={{ ...th, textAlign: "left" }}>Môn</th>
              <th style={th}>Cấu hình (tiết)</th>
              <th style={th}>Thực xếp (tiết)</th>
              <th style={th}>Chênh lệch</th>
            </tr>
          </thead>
          <tbody>
            {(onlyMismatched ? mismatched : rows).length === 0 ? (
              <tr><td colSpan={5} style={{ ...td, color: "#6b7280", padding: 16 }}>Không có (lớp, môn) nào bị thiếu/thừa tiết.</td></tr>
            ) : (onlyMismatched ? mismatched : rows).map((r) => {
              const rowStyle = r.diff === 0 ? {} : { background: r.diff < 0 ? "#fee2e2" : "#fef9c3" };
              return (
                <tr key={`${r.classId}|${r.subjectId}`} style={rowStyle}>
                  <td style={{ ...td, fontWeight: 600 }}>{r.className}</td>
                  <td style={{ ...td, textAlign: "left" }}>{r.subjectName}</td>
                  <td style={td}>{r.planned}</td>
                  <td style={td}>{r.scheduled}</td>
                  <td style={{ ...td, fontWeight: r.diff !== 0 ? 700 : 400, color: r.diff < 0 ? "#b91c1c" : r.diff > 0 ? "#a16207" : "#374151" }}>
                    {r.diff > 0 ? `+${r.diff}` : r.diff}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const statBox = { padding: "8px 14px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#f9fafb", fontSize: 13 };
const filterBtn = { padding: "8px 14px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const filterBtnActive = { ...filterBtn, background: "#0ea5a3", color: "#fff", border: "1px solid #0ea5a3" };
const th = { border: "1px solid #e5e7eb", padding: "6px 8px", background: "#f9fafb", whiteSpace: "nowrap", textAlign: "center" };
const td = { border: "1px solid #e5e7eb", padding: "6px 8px", textAlign: "center" };

export default HappySchoolScheduleStats;
